import type { CartItemReference } from '../models';
import { uuidGenerator } from '../utils/uuid-generator';
import { NewCartItemIdGenerator } from '../utils/new-cart-item-id-generator';
import { type ProductModel } from '@kidslabs/module-catalog';
import { type CartItemWithTypeInterface } from '../models/cart.model';
import type { ConfigurableCartItem } from '../types/front.graphql';
import { cartStoreAPI } from '../stores/cart-store.public-api';
import { cartItemReferenceStoreAPI } from '../stores/cart-item-reference-store.public-api';

/**
 * ============================================================================
 * CART ITEM REFERENCE RECONCILIATION SERVICE
 * ============================================================================
 * Handles local-first reconciliation between server cart items and local references
 * This service ensures data consistency without requiring server-side support
 *
 * ARCHITECTURE CHANGE:
 * - Service now gets data directly from stores via public APIs
 * - No need to pass serverCartItems, localReferences, currentCartId from outside
 * - Single source of truth: Store is the only data source
 * - Reduces prop drilling and ensures data consistency
 *
 * RECONCILIATION LOGIC:
 *
 * STEP 1: Process server items → match with local references by cartItemId
 * - For each server item, find all local references with matching cartItemId
 * - Select ONE reference to set isSelected = true based on priority:
 *   Priority 1: No logics (no gifts/attachments)
 *   Priority 2: No parentCartItemReferenceId
 *   Priority 3: Others
 * - If already selected, leave it. Otherwise, set first match by priority to selected
 * - Update matched reference with server data (qty, cartItems)
 *
 * STEP 2: Process selected local references (not matched in Step 1)
 * - If cartItemId doesn't match any server item → leave as is (keep selected)
 * - If productId matches a server item → update cartItemId and cartId from server
 *
 * STEP 3: Process unselected local references (not matched in Step 1)
 * - Check if they can match any remaining server items by productId
 * - Apply same priority rules as Step 1
 *
 * STEP 4: Create new references for unmatched server items
 * - Any server item not matched in previous steps → create new reference (selected = true)
 * - EXCEPTION: Gift items (price = 0) are separated and NOT created as references
 *
 * SPECIAL HANDLING:
 * - ConfigurableCartItem: productId from configured_variant.id, parentProduct from product
 * - Gift items (price = 0): Detected and separated, NOT created as references (for 1-n logic assignment later)
 */

export interface ReconciliationResult {
    // Final reconciled references
    references: CartItemReference[];

    // Statistics for debugging and logging
    stats: ReconciliationStats;

    // Notifications for user feedback
    notifications: ReconciliationNotification[];

    // Gift items (price = 0) - separated for later logic assignment
    giftItems: CartItemWithTypeInterface[];
}

export interface ReconciliationStats {
    matched: number; // References matched with cart items
    created: number; // New references created
    updated: number; // References updated (quantity changes)
    deleted: number; // References deleted (stale)
    preserved: number; // Selected state preserved
    giftsDetected: number; // Gift items detected (price = 0)
}

export interface ReconciliationNotification {
    type: 'info' | 'warning' | 'error';
    message: string;
    itemsAffected: number;
}

export class CartItemReferenceReconciliationService {
    /**
     * Extract product information from cart item
     * Handles ConfigurableCartItem specially:
     * - productId: from configured_variant.id (the selected variant)
     * - parentProduct: from product (the configurable parent)
     */
    private extractProductInfo(cartItem: CartItemWithTypeInterface): {
        productId: number;
        parentProduct?: ProductModel;
    } {
        // Check if this is a ConfigurableCartItem
        if (cartItem.__typename === 'ConfigurableCartItem') {
            const configurableItem = cartItem as ConfigurableCartItem;

            // For configurable items:
            // - productId is the variant ID from configured_variant
            // - parentProduct is the configurable product
            const variantId = configurableItem.configured_variant?.id;

            if (typeof variantId !== 'number') {
                console.error('[extractProductInfo] Invalid configured_variant ID for ConfigurableCartItem', {
                    cartItemId: cartItem.id,
                    variantId,
                });
                // Fallback to product.id if configured_variant is missing
                return {
                    productId: typeof cartItem.product.id === 'number' ? cartItem.product.id : 0,
                    parentProduct: undefined,
                };
            }

            return {
                productId: variantId,
                parentProduct: cartItem.product, // The configurable parent product
            };
        }

        // For other cart item types (SimpleCartItem, etc.)
        // productId is directly from product.id
        const productId = cartItem.product.id;

        if (typeof productId !== 'number') {
            console.error('[extractProductInfo] Invalid product ID', {
                cartItemId: cartItem.id,
                typename: cartItem.__typename,
            });
            return {
                productId: 0,
                parentProduct: undefined,
            };
        }

        return {
            productId,
            parentProduct: undefined,
        };
    }

    /**
     * Calculate priority score for reference selection
     * Lower score = higher priority
     */
    private calculatePriority(ref: CartItemReference): number {
        // Priority 1: No logics (score = 1)
        if (!ref.logics || ref.logics.length === 0) {
            return 1;
        }

        // Priority 2: No parent reference (score = 2)
        if (!ref.parentCartItemReferenceId) {
            return 2;
        }

        // Priority 3: Others (score = 3)
        return 3;
    }

    /**
     * Sort references by priority
     */
    private sortByPriority(refs: CartItemReference[]): CartItemReference[] {
        return [...refs].sort((a, b) => {
            const priorityA = this.calculatePriority(a);
            const priorityB = this.calculatePriority(b);

            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }

            // If same priority, prefer already selected
            if (a.isSelected && !b.isSelected) return -1;
            if (!a.isSelected && b.isSelected) return 1;

            // If same priority and selection status, keep original order
            return 0;
        });
    }

    /**
     * Reconcile server cart items with local references
     *
     * ARCHITECTURE: Gets data directly from store instead of parameters
     * - serverCartItems: from cartStoreAPI.state.getItems()
     * - localReferences: from cartItemReferenceStoreAPI.state.getItems()
     * - currentCartId: from cartStoreAPI.state.getCartId()
     *
     * Returns updated references that should be persisted
     */
    async reconcile(): Promise<ReconciliationResult> {
        // Get data directly from store via public API
        const serverCartItems = cartStoreAPI.state.getItems() as CartItemWithTypeInterface[];
        const localReferences = cartItemReferenceStoreAPI.state.getItems() as CartItemReference[];
        const currentCartId = cartStoreAPI.state.getCartId();

        if (!currentCartId) {
            console.warn('[ReconciliationService] No cart ID available, returning empty result');
            return {
                giftItems: [],
                references: localReferences,
                stats: {
                    matched: 0,
                    created: 0,
                    updated: 0,
                    deleted: 0,
                    preserved: 0,
                    giftsDetected: 0,
                },
                notifications: [{ type: 'warning', message: 'Cart ID not available', itemsAffected: 0 }],
            };
        }

        console.log('[ReconciliationService] Starting reconciliation with priority logic', {
            serverItems: serverCartItems.length,
            localRefs: localReferences.length,
            cartId: currentCartId,
        });

        const stats: ReconciliationStats = {
            matched: 0,
            created: 0,
            updated: 0,
            deleted: 0,
            preserved: 0,
            giftsDetected: 0,
        };

        const notifications: ReconciliationNotification[] = [];
        const finalReferences: CartItemReference[] = [];
        const giftItems: CartItemWithTypeInterface[] = [];

        // Track which references and server items have been processed
        const processedReferenceIds = new Set<string>();
        const processedServerItemIds = new Set<string>();

        // Build map of server cart items by cartItemId for quick lookup
        const serverCartItemsMap = new Map<string, CartItemWithTypeInterface>();
        const serverCartItemsByProductId = new Map<number, CartItemWithTypeInterface[]>();

        serverCartItems.forEach(item => {
            serverCartItemsMap.set(item.id, item);

            // Extract product info (handles ConfigurableCartItem specially)
            const { productId } = this.extractProductInfo(item);

            if (productId !== 0) {
                if (!serverCartItemsByProductId.has(productId)) {
                    serverCartItemsByProductId.set(productId, []);
                }
                serverCartItemsByProductId.get(productId)!.push(item);
            }
        });

        // ========== STEP 1: Process server items and match with local references by cartItemId ==========
        console.log('[ReconciliationService] STEP 1: Processing server items with cartItemId matching');

        serverCartItems.forEach(serverItem => {
            // Check if this is a gift item (price = 0)
            const itemPrice = serverItem.prices?.price?.value ?? 0;
            if (itemPrice === 0) {
                console.log(
                    '[ReconciliationService] Detected gift item (price = 0), separating for later logic assignment',
                    {
                        cartItemId: serverItem.id,
                        typename: serverItem.__typename,
                        price: itemPrice,
                    }
                );

                giftItems.push(serverItem);
                stats.giftsDetected++;
                processedServerItemIds.add(serverItem.id);
                return;
            }

            const matchingRefs = localReferences.filter(ref => ref.cartItemId === serverItem.id);

            if (matchingRefs.length === 0) {
                // No matching reference found, will handle in Step 4
                return;
            }

            console.log('[ReconciliationService] Found matching references for server item', {
                serverItemId: serverItem.id,
                matchingRefsCount: matchingRefs.length,
            });

            // Sort by priority
            const sortedRefs = this.sortByPriority(matchingRefs);

            // Check if any is already selected
            const alreadySelected = sortedRefs.find(ref => ref.isSelected);

            if (alreadySelected) {
                // Keep the already selected reference
                console.log('[ReconciliationService] Keeping already selected reference', {
                    refId: alreadySelected.cartItemReferenceId,
                    priority: this.calculatePriority(alreadySelected),
                });

                finalReferences.push({
                    ...alreadySelected,
                    cartId: currentCartId,
                    cartItemId: serverItem.id,
                    qty: serverItem.quantity,
                    isSelected: true,
                    cartItem: serverItem,
                });

                stats.preserved++;
                stats.matched++;
                processedReferenceIds.add(alreadySelected.cartItemReferenceId);
                processedServerItemIds.add(serverItem.id);

                // Add other matching references as unselected
                sortedRefs.forEach(ref => {
                    if (ref.cartItemReferenceId !== alreadySelected.cartItemReferenceId) {
                        finalReferences.push({
                            ...ref,
                            cartId: undefined,
                            cartItemId: NewCartItemIdGenerator.generateFromReference(
                                ref.cartItem?.product?.id ?? '',
                                ref.parentProduct
                            ),
                            isSelected: false,
                        });
                        processedReferenceIds.add(ref.cartItemReferenceId);
                    }
                });
            } else {
                // Select the first one by priority
                const selectedRef = sortedRefs[0];

                console.log('[ReconciliationService] Selecting reference by priority', {
                    refId: selectedRef.cartItemReferenceId,
                    priority: this.calculatePriority(selectedRef),
                    hasLogics: (selectedRef.logics?.length ?? 0) > 0,
                    hasParent: !!selectedRef.parentCartItemReferenceId,
                });

                finalReferences.push({
                    ...selectedRef,
                    cartId: currentCartId,
                    cartItemId: serverItem.id,
                    qty: serverItem.quantity,
                    isSelected: true,
                    cartItem: serverItem,
                });

                stats.matched++;
                processedReferenceIds.add(selectedRef.cartItemReferenceId);
                processedServerItemIds.add(serverItem.id);

                // Add other matching references as unselected
                sortedRefs.slice(1).forEach(ref => {
                    finalReferences.push({
                        ...ref,
                        cartId: undefined,
                        cartItemId: NewCartItemIdGenerator.generateFromReference(
                            ref.cartItem?.product?.id ?? '',
                            ref.parentProduct
                        ),
                        isSelected: false,
                    });
                    processedReferenceIds.add(ref.cartItemReferenceId);
                });
            }
        });

        // ========== STEP 2: Process selected local references not matched in Step 1 ==========
        console.log('[ReconciliationService] STEP 2: Processing remaining selected references');

        const remainingSelectedRefs = localReferences.filter(
            ref => ref.isSelected && !processedReferenceIds.has(ref.cartItemReferenceId)
        );

        remainingSelectedRefs.forEach(ref => {
            // Check if cartItemId matches any server item
            const serverItem = ref.cartItemId ? serverCartItemsMap.get(ref.cartItemId) : null;

            if (serverItem) {
                // This case should have been handled in Step 1, but just in case
                console.warn('[ReconciliationService] Selected ref with matching cartItemId not processed in Step 1', {
                    refId: ref.cartItemReferenceId,
                    cartItemId: ref.cartItemId,
                });

                finalReferences.push({
                    ...ref,
                    cartId: currentCartId,
                    cartItemId: serverItem.id,
                    qty: serverItem.quantity,
                    isSelected: true,
                    cartItem: serverItem,
                });

                stats.matched++;
                processedReferenceIds.add(ref.cartItemReferenceId);
                processedServerItemIds.add(serverItem.id);
            } else {
                // cartItemId doesn't match any server item
                // Check if productId matches any unprocessed server item
                const matchingServerItems = serverCartItemsByProductId.get(ref.productId) || [];
                const unprocessedServerItem = matchingServerItems.find(item => !processedServerItemIds.has(item.id));

                if (unprocessedServerItem) {
                    // Found matching server item by productId → update cartItemId and cartId
                    console.log('[ReconciliationService] Updating selected ref with matching productId', {
                        refId: ref.cartItemReferenceId,
                        oldCartItemId: ref.cartItemId,
                        newCartItemId: unprocessedServerItem.id,
                        productId: ref.productId,
                    });

                    finalReferences.push({
                        ...ref,
                        cartId: currentCartId,
                        cartItemId: unprocessedServerItem.id,
                        qty: unprocessedServerItem.quantity,
                        isSelected: true,
                        cartItem: unprocessedServerItem,
                    });

                    stats.updated++;
                    processedReferenceIds.add(ref.cartItemReferenceId);
                    processedServerItemIds.add(unprocessedServerItem.id);
                } else {
                    // No matching server item found, keep selected state
                    console.log('[ReconciliationService] Keeping selected ref without server match', {
                        refId: ref.cartItemReferenceId,
                        cartItemId: ref.cartItemId,
                        productId: ref.productId,
                    });

                    finalReferences.push({
                        ...ref,
                        isSelected: true,
                    });

                    stats.preserved++;
                    processedReferenceIds.add(ref.cartItemReferenceId);
                }
            }
        });

        // ========== STEP 3: Process unselected local references not matched in Step 1 ==========
        console.log('[ReconciliationService] STEP 3: Processing remaining unselected references');

        const remainingUnselectedRefs = localReferences.filter(
            ref => !ref.isSelected && !processedReferenceIds.has(ref.cartItemReferenceId)
        );

        // Group unselected refs by productId
        const unselectedByProductId = new Map<number, CartItemReference[]>();
        remainingUnselectedRefs.forEach(ref => {
            if (!unselectedByProductId.has(ref.productId)) {
                unselectedByProductId.set(ref.productId, []);
            }
            unselectedByProductId.get(ref.productId)!.push(ref);
        });

        // Try to match with remaining server items by productId
        unselectedByProductId.forEach((refs, productId) => {
            const matchingServerItems = serverCartItemsByProductId.get(productId) || [];
            const unprocessedServerItems = matchingServerItems.filter(item => !processedServerItemIds.has(item.id));

            if (unprocessedServerItems.length > 0) {
                // Apply priority rules to select which reference to match
                const sortedRefs = this.sortByPriority(refs);

                unprocessedServerItems.forEach((serverItem, index) => {
                    if (index < sortedRefs.length) {
                        const selectedRef = sortedRefs[index];

                        console.log('[ReconciliationService] Matching unselected ref with server item by productId', {
                            refId: selectedRef.cartItemReferenceId,
                            serverItemId: serverItem.id,
                            productId,
                            priority: this.calculatePriority(selectedRef),
                        });

                        finalReferences.push({
                            ...selectedRef,
                            cartId: currentCartId,
                            cartItemId: serverItem.id,
                            qty: serverItem.quantity,
                            isSelected: true,
                            cartItem: serverItem,
                        });

                        stats.matched++;
                        processedReferenceIds.add(selectedRef.cartItemReferenceId);
                        processedServerItemIds.add(serverItem.id);
                    }
                });

                // Add remaining unmatched references as unselected
                sortedRefs.slice(unprocessedServerItems.length).forEach(ref => {
                    finalReferences.push({
                        ...ref,
                        cartId: undefined,
                        cartItemId: NewCartItemIdGenerator.generateFromReference(
                            ref.cartItem?.product?.id ?? '',
                            ref.parentProduct
                        ),
                        isSelected: false,
                    });
                    processedReferenceIds.add(ref.cartItemReferenceId);
                });
            } else {
                // No matching server items, keep all as unselected
                refs.forEach(ref => {
                    finalReferences.push({
                        ...ref,
                        cartId: undefined,
                        cartItemId: NewCartItemIdGenerator.generateFromReference(
                            ref.cartItem?.product?.id ?? '',
                            ref.parentProduct
                        ),
                        isSelected: false,
                    });
                    processedReferenceIds.add(ref.cartItemReferenceId);
                });
            }
        });

        // ========== STEP 4: Create new references for unprocessed server items ==========
        console.log('[ReconciliationService] STEP 4: Creating references for unmatched server items');

        serverCartItems.forEach(cartItem => {
            if (processedServerItemIds.has(cartItem.id)) {
                return;
            }

            // Extract product info (handles ConfigurableCartItem specially)
            const { productId, parentProduct } = this.extractProductInfo(cartItem);

            if (productId === 0) {
                console.error('[ReconciliationService] Invalid product ID', { cartItemId: cartItem.id });
                return;
            }

            // Gift items already separated in Step 1, skip
            const itemPrice = cartItem.prices?.price?.value ?? 0;
            if (itemPrice === 0) {
                return;
            }

            console.log('[ReconciliationService] Creating new reference for unmatched server item', {
                cartItemId: cartItem.id,
                productId,
                typename: cartItem.__typename,
                hasParentProduct: !!parentProduct,
                price: itemPrice,
            });

            // Derive variant SKU for configurable products
            let sku: string | undefined;
            if (cartItem.__typename === 'ConfigurableCartItem') {
                const configurableItem = cartItem as ConfigurableCartItem;
                sku = configurableItem.configured_variant?.sku ?? cartItem.product?.sku ?? undefined;
            } else {
                sku = cartItem.product?.sku ?? undefined;
            }

            const newReference: CartItemReference = {
                cartItemReferenceId: uuidGenerator.generate(),
                productId,
                cartId: currentCartId,
                cartItemId: cartItem.id,
                qty: cartItem.quantity,
                logics: [],
                isSelected: true,
                createdAt: new Date().toISOString(),
                cartItem: cartItem,
                parentProduct, // Store parent product for configurable items
                sku, // Variant SKU for configurable, product SKU for simple
            };

            finalReferences.push(newReference);
            stats.created++;
        });

        // ========== Calculate statistics ==========
        const deletedCount = localReferences.length - (finalReferences.length - stats.created);
        stats.deleted = deletedCount > 0 ? deletedCount : 0;

        console.log('[ReconciliationService] Reconciliation complete with priority logic', {
            stats,
            processedRefs: processedReferenceIds.size,
            processedServerItems: processedServerItemIds.size,
            finalRefsCount: finalReferences.length,
            giftItemsCount: giftItems.length,
        });

        // Add notifications
        if (stats.created > 0) {
            notifications.push({
                type: 'info',
                message: `${stats.created} items added to cart`,
                itemsAffected: stats.created,
            });
        }

        if (stats.deleted > 0) {
            notifications.push({
                type: 'warning',
                message: `${stats.deleted} items removed from cart`,
                itemsAffected: stats.deleted,
            });
        }

        if (stats.updated > 0) {
            notifications.push({
                type: 'info',
                message: `${stats.updated} items updated`,
                itemsAffected: stats.updated,
            });
        }

        if (stats.giftsDetected > 0) {
            notifications.push({
                type: 'info',
                message: `${stats.giftsDetected} gift items detected (will be assigned logic later)`,
                itemsAffected: stats.giftsDetected,
            });
        }

        return {
            references: finalReferences,
            stats,
            notifications,
            giftItems, // Return separated gift items for later logic assignment
        };
    }

    /**
     * Handle reconciliation notifications
     * Can be extended to show UI notifications
     */
    handleNotifications(result: ReconciliationResult): void {
        result.notifications.forEach(notification => {
            switch (notification.type) {
                case 'error':
                    console.error('[ReconciliationService]', notification.message);
                    break;
                case 'warning':
                    console.warn('[ReconciliationService]', notification.message);
                    break;
                case 'info':
                default:
                    console.info('[ReconciliationService]', notification.message);
                    break;
            }
        });
    }

    /**
     * Validate references before sync
     * Ensures data integrity before sending to server
     */
    validateReferencesForSync(references: CartItemReference[]): {
        valid: CartItemReference[];
        invalid: CartItemReference[];
        errors: string[];
    } {
        const valid: CartItemReference[] = [];
        const invalid: CartItemReference[] = [];
        const errors: string[] = [];

        references.forEach(ref => {
            // Validation rules
            if (!ref.productId || ref.productId <= 0) {
                invalid.push(ref);
                errors.push(`Invalid productId for reference ${ref.cartItemReferenceId}`);
                return;
            }

            if (!ref.qty || ref.qty <= 0) {
                invalid.push(ref);
                errors.push(`Invalid quantity for reference ${ref.cartItemReferenceId}`);
                return;
            }

            // if (!ref.cartId) {
            //     invalid.push(ref);
            //     errors.push(`Missing cartId for reference ${ref.cartItemReferenceId}`);
            //     return;
            // }

            // Valid reference
            valid.push(ref);
        });

        if (errors.length > 0) {
            console.warn('[ReconciliationService] Validation errors', {
                validCount: valid.length,
                invalidCount: invalid.length,
                errors,
            });
        }

        return { valid, invalid, errors };
    }

    /**
     * Get statistics about current references
     * Useful for debugging and monitoring
     */
    getStatistics(references: CartItemReference[]): {
        total: number;
        selected: number;
        unselected: number;
        withLogics: number;
        withoutLogics: number;
        withCartItemId: number;
        withoutCartItemId: number;
    } {
        return {
            total: references.length,
            selected: references.filter(r => r.isSelected).length,
            unselected: references.filter(r => !r.isSelected).length,
            withLogics: references.filter(r => r.logics && r.logics.length > 0).length,
            withoutLogics: references.filter(r => !r.logics || r.logics.length === 0).length,
            withCartItemId: references.filter(r => !!r.cartItemId).length,
            withoutCartItemId: references.filter(r => !r.cartItemId).length,
        };
    }

    /**
     * Clean up stale references
     * Removes references that are no longer valid
     */
    cleanupStaleReferences(references: CartItemReference[], validCartItemIds: Set<string>): CartItemReference[] {
        return references.filter(ref => {
            // Keep references without cartItemId (new items)
            if (!ref.cartItemId) {
                return true;
            }

            // Keep references with valid cartItemId
            if (validCartItemIds.has(ref.cartItemId)) {
                return true;
            }

            // Remove stale reference
            console.log('[ReconciliationService] Removing stale reference', {
                refId: ref.cartItemReferenceId,
                cartItemId: ref.cartItemId,
                productId: ref.productId,
            });
            return false;
        });
    }

    /**
     * Merge duplicate references
     * Combines references for the same product with same logics
     */
    mergeDuplicateReferences(references: CartItemReference[]): CartItemReference[] {
        const merged = new Map<string, CartItemReference>();

        references.forEach(ref => {
            // Create unique key based on productId and logics
            const logicIds = (ref.logics || [])
                .map(l => l.logicId)
                .sort()
                .join(',');
            const key = `${ref.productId}-${logicIds}`;

            const existing = merged.get(key);
            if (existing) {
                // Merge quantities
                existing.qty += ref.qty;

                // Preserve selected state if any reference is selected
                if (ref.isSelected) {
                    existing.isSelected = true;
                }

                console.log('[ReconciliationService] Merged duplicate reference', {
                    key,
                    newQty: existing.qty,
                    merged: ref.cartItemReferenceId,
                    into: existing.cartItemReferenceId,
                });
            } else {
                merged.set(key, { ...ref });
            }
        });

        return Array.from(merged.values());
    }

    /**
     * Reset all selected states
     * Useful for "unselect all" functionality
     */
    resetAllSelectedStates(references: CartItemReference[]): CartItemReference[] {
        return references.map(ref => ({
            ...ref,
            isSelected: false,
        }));
    }

    /**
     * Select all references
     * Useful for "select all" functionality
     */
    selectAllReferences(references: CartItemReference[]): CartItemReference[] {
        return references.map(ref => ({
            ...ref,
            isSelected: true,
        }));
    }

    /**
     * Toggle selection for specific references
     */
    toggleReferenceSelection(references: CartItemReference[], referenceIds: string[]): CartItemReference[] {
        const idsSet = new Set(referenceIds);

        return references.map(ref => {
            if (idsSet.has(ref.cartItemReferenceId)) {
                return {
                    ...ref,
                    isSelected: !ref.isSelected,
                };
            }
            return ref;
        });
    }
}
